import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { OkResponse, User } from '@cloudsign/shared';
import { api } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { t } from '../../lib/i18n';
import { useCan } from '../../lib/session';
import { useToastOnSuccess } from '../../components/Toast';
import { Alert, Badge, Button, Card, ConfirmButton, PageHeader, SelectField, Spinner, TextField } from '../../components/ui';
import { ErrorLine, ErrorPanel, fieldErrors } from '../../components/ErrorPanel';
import { statusTone, useRoles } from './UsersPage';

export function UserDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const canManage = useCan('users.manage');
  const canDelete = useCan('users.delete');
  const canRemove2fa = useCan('users.remove_2fa');
  const roles = useRoles();
  const q = useQuery({ queryKey: ['users', id], queryFn: () => api.get(`/users/${id}`, { schema: User }) });

  const [fullName, setFullName] = useState('');
  const [roleId, setRoleId] = useState('');
  const [status, setStatus] = useState<'active' | 'disabled'>('active');
  useEffect(() => {
    if (q.data) {
      setFullName(q.data.fullName);
      setRoleId(q.data.roleId);
      setStatus(q.data.status === 'disabled' ? 'disabled' : 'active');
    }
  }, [q.data]);

  const save = useMutation({
    mutationFn: () => api.patch(`/users/${id}`, { body: { fullName, roleId, status: q.data?.status === 'invited' && status === 'active' ? undefined : status }, schema: User }),
    onSuccess: async (u) => {
      qc.setQueryData(['users', id], u);
      await qc.invalidateQueries({ queryKey: ['users'] });
    },
  });
  useToastOnSuccess(save.isSuccess, t('common.saved'));
  const action = useMutation({
    mutationFn: (path: string) => api.post(`/users/${id}/${path}`, { schema: OkResponse }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
  useToastOnSuccess(action.isSuccess, t('users.actionDone'));
  const remove = useMutation({
    mutationFn: () => api.delete(`/users/${id}`),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['users'] });
      void navigate('/admin/users', { replace: true });
    },
  });

  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />;
  const u = q.data;
  const errs = fieldErrors(save.error);
  const selectedRole = roles.data?.find((r) => r.id === roleId);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };

  return (
    <>
      <PageHeader title={u.fullName} actions={<Link className="text-brand hover:underline" to="/admin/users">{t('common.back')}</Link>} />
      <div className="space-y-6">
        <Card title={t('users.details')}>
          <dl className="mb-4 grid gap-2 text-sm sm:grid-cols-2">
            <div>
              <dt className="font-medium">{t('auth.email')}</dt>
              <dd>{u.email}</dd>
            </div>
            <div>
              <dt className="font-medium">{t('common.status')}</dt>
              <dd>
                <Badge tone={statusTone(u.status)}>{t(`status.${u.status}`)}</Badge>
              </dd>
            </div>
            <div>
              <dt className="font-medium">{t('users.twoFactor')}</dt>
              <dd>{u.twoFactorEnabled ? t('common.yes') : t('common.no')}</dd>
            </div>
            <div>
              <dt className="font-medium">{t('users.lastLogin')}</dt>
              <dd>{u.lastLoginAt ? formatDateTime(u.lastLoginAt) : t('users.never')}</dd>
            </div>
          </dl>
          <form onSubmit={submit} className="max-w-md space-y-4">
            <ErrorLine error={save.error} />
                        <TextField label={t('auth.fullName')} required value={fullName} disabled={!canManage} onChange={(e) => setFullName(e.target.value)} error={errs['fullName']} />
            <SelectField label={t('users.role')} value={roleId} disabled={!canManage} onChange={(e) => setRoleId(e.target.value)} error={errs['roleId']}>
              {(roles.data ?? []).map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </SelectField>
            {selectedRole?.siteScope === 'assigned' ? <Alert kind="info">{t('users.sitesLater')}</Alert> : null}
            {u.status !== 'invited' ? (
              <SelectField
                label={t('common.status')}
                value={status}
                disabled={!canManage}
                onChange={(e) => setStatus(e.target.value === 'disabled' ? 'disabled' : 'active')}
              >
                <option value="active">{t('status.active')}</option>
                <option value="disabled">{t('status.disabled')}</option>
              </SelectField>
            ) : null}
            {canManage ? (
              <div className="flex justify-end"><Button type="submit" variant="primary" disabled={save.isPending}>
                {t('common.save')}
              </Button></div>
            ) : null}
          </form>
        </Card>

        {canManage || canRemove2fa || canDelete ? (
          <Card title={t('users.actions')}>
            <div className="space-y-4">
              <ErrorLine error={action.error ?? remove.error} />
                            <div className="flex flex-wrap items-center gap-2">
                {canManage && u.status === 'invited' ? (
                  <Button disabled={action.isPending} onClick={() => action.mutate('resend-invite')}>
                    {t('users.resendInvite')}
                  </Button>
                ) : null}
                {canManage && u.status !== 'invited' ? (
                  <Button disabled={action.isPending} onClick={() => action.mutate('send-password-reset')}>
                    {t('users.sendPasswordReset')}
                  </Button>
                ) : null}
                {canRemove2fa && u.twoFactorEnabled ? (
                  <ConfirmButton label={t('users.remove2fa')} pending={action.isPending} onConfirm={() => action.mutate('remove-2fa')} />
                ) : null}
                {canDelete ? <ConfirmButton label={t('users.delete')} pending={remove.isPending} onConfirm={() => remove.mutate()} /> : null}
              </div>
            </div>
          </Card>
        ) : null}
      </div>
    </>
  );
}
